import { useState, useCallback, useMemo } from "react";
import { ToolContainer, FileDropZone, ProcessingState, DownloadButton, ResultPreview } from "@/components/ToolLayout";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { decodeImage, detectImageKind, renamedTo } from "./decode";
import { estimateJpegQuality, estimateByBitsPerPixel } from "./imageFormats";
import { convertImageInBrowser } from "./convertBrowser";
import { formatInfo, parseConvertOptions, parseFormat } from "./convertCore";
import { DEFAULT_FORM, defaultTargetFor, formToRaw, friendlyOptionError, metaCanBeKept, needsFill, type ConvertForm } from "./convertForm";
import { FillNotice, FormatPicker, FormatSpecificControls, MoreOptions, QualityControl, formatUnavailable, optionError as formOptionError, useEncodeSupport } from "./ConvertOptionsPanel";

// Formats whose source file can genuinely have a transparent background.
const SOURCE_TYPES_WITH_ALPHA = new Set(["image/png", "image/webp", "image/gif", "image/svg+xml", "image/avif"]);

// Pre-select the conversion people most likely want, so the default is never "same format".
const defaultTarget = (f: File) => defaultTargetFor(f.type, detectImageKind(f));

const TOOL_INFO_CONTENT = {
  what: `Converts an image between PNG, JPEG, WebP, AVIF, GIF, BMP, ICO and TIFF. Different websites, print shops, programs and operating systems expect different formats, so the same picture often has to exist in more than one. It can also resize the picture, turn or flip it, make it black and white, set a print resolution, and remove or keep camera and location data, all in the same step.`,
  how: `Upload your image, choose the format you want, change any option you need, and press Convert. Converting a picture with a transparent background to JPEG or 24-bit BMP shows a fill colour picker first, since those formats cannot be see-through; the transparent areas get the colour you pick (white by default) rather than turning black. Icons (ICO) can hold several sizes in one file: tick the sizes you want.`,
  tip: `Use PNG for logos, icons and screenshots, JPEG for photos where file size matters, WebP when the destination accepts it and a small file is the priority, and ICO for a website favicon. A transparent picture saved as JPEG loses its transparency for good, so keep the original if you might need it again. Options that need a different kind of encoder, such as lossless WebP, progressive JPEG or PNG palettes, are available through the FreeDailyPro API.`,
};

export default function ConvertImage() {
  const [file, setFile] = useState<File | null>(null);
  const [form, setForm] = useState<ConvertForm>(DEFAULT_FORM);
  const [processing, setProcessing] = useState(false);
  const [result, setResult] = useState<Blob | null>(null);
  const [openError, setOpenError] = useState<string | null>(null);
  const canEncode = useEncodeSupport();
  const [sourceEstimate, setSourceEstimate] = useState<number | null>(null);

  const { format } = form;
  const info = formatInfo(format);
  const change = useCallback((patch: Partial<ConvertForm>) => { setForm((f) => ({ ...f, ...patch })); setResult(null); }, []);

  /** Estimates the source's quality (JPEG: from its quantisation table; WebP: from bytes per pixel). */
  const estimateSource = async (f: File, canvas: HTMLCanvasElement): Promise<number | null> => {
    try {
      if (f.type === "image/jpeg") return estimateJpegQuality(new Uint8Array(await f.slice(0, 65536).arrayBuffer()));
      if (f.type === "image/webp") return estimateByBitsPerPixel(f.size, canvas.width, canvas.height);
    } catch { /* no estimate */ }
    return null;
  };

  const handleFiles = useCallback((files: File[]) => {
    const f = files[0];
    if (!f) return;
    // Open it first so an unreadable file is reported at once, with a real reason.
    setOpenError(null);
    decodeImage(f).then(async ({ canvas }) => { setSourceEstimate(await estimateSource(f, canvas)); setFile(f); setResult(null); setForm((cur) => ({ ...cur, format: defaultTarget(f) })); })
      .catch((e) => {
        const msg = `${e?.message || "This file could not be opened as an image"} Nothing was used from your trial.`;
        setOpenError(msg);
        toast.error(msg);
      });
  }, []);

  // The needs-a-fill-colour rule: JPEG and 24-bit BMP cannot be see-through.
  const mayHaveAlpha = !!file && SOURCE_TYPES_WITH_ALPHA.has(file.type);

  const parsed = useMemo(() => parseConvertOptions(formToRaw(form)), [form]);
  const optionError = formOptionError(form, parsed.ok ? null : friendlyOptionError(parsed.error));

  const convert = async () => {
    if (!file || !parsed.ok) return;
    setProcessing(true);
    try {
      const blob = await convertImageInBrowser(file, parsed.options);
      setResult(blob);
      toast.success("Image converted!");
    } catch (e) { toast.error((e as Error)?.message || "Failed to convert"); }
    finally { setProcessing(false); }
  };

  const ext = formatInfo(parseFormat(result?.type) ?? format).ext;
  const keepable = !!file && metaCanBeKept(format, file.type);

  return (
    <ToolContainer>
      {!file ? (
        <FileDropZone gateAtDownload accept="image/*" onFiles={handleFiles} label="Drop an image to convert" description="PNG, JPEG, WebP - also opens HEIC (iPhone), AVIF, TIFF, GIF, BMP, SVG" />
      ) : null}
      {!file && openError && <p role="alert" className="mt-3 text-sm text-destructive">{openError}</p>}
      {!file ? null : (
        <div className="space-y-4">
          <div className="p-4 rounded-lg border bg-card"><p className="text-sm font-medium">{file.name}</p></div>

          <FormatPicker form={form} onChange={change} support={canEncode} />
          <QualityControl form={form} onChange={change} sourceEstimate={sourceEstimate} />
          <FormatSpecificControls form={form} onChange={change} />
          {file.type === "image/gif" && format !== "gif" && <p className="text-xs text-muted-foreground">Animated GIFs: only the first frame is converted.</p>}
          <FillNotice form={form} onChange={change} show={needsFill(form) && mayHaveAlpha} />
          <MoreOptions form={form} onChange={change} metaHelp={keepable ? "Copied into the new JPEG." : "Only possible when both the original and the result are JPEG. Otherwise this information is removed."} />

          {optionError && <p role="alert" className="text-sm text-destructive">{optionError}</p>}

          {result && <ResultPreview blob={result} />}
          {result && <p className="text-xs text-muted-foreground" data-testid="convert-size">Result: {(result.size / 1024).toFixed(0)} KB (original {(file.size / 1024).toFixed(0)} KB){result.size > file.size * 1.5 ? `. This is ${(result.size / file.size).toFixed(1)}x larger than the original${format !== "webp" && format !== "avif" ? "; WebP is usually smaller" : ""}.` : ""}</p>}
          <div className="flex gap-3">
            {processing ? <ProcessingState /> : result ? (
              <><DownloadButton blob={result} fileName={renamedTo(file.name, ext)} /><Button variant="outline" onClick={() => { setFile(null); setResult(null); }}>New</Button></>
            ) : (
              <><Button onClick={convert} disabled={!parsed.ok || !!optionError || formatUnavailable(canEncode, format)}>Convert</Button><Button variant="outline" onClick={() => setFile(null)}>Reset</Button></>
            )}
          </div>
        </div>
      )}
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
